import { motion } from "framer-motion";
import FadeIn from "@/components/FadeIn";

const CHAPTERS = [
  {
    n: "01",
    kicker: "Le constat",
    title: "L'énergie fossile devient un luxe",
    text: "Prix du gaz, prix du fioul : votre facture ne fait qu'augmenter, et votre chaudière vieillissante consomme toujours plus pour le même confort. Chaque hiver qui passe, c'est de l'argent qui part en fumée.",
    stat: "70 %",
    statLabel: "d'économies sur votre facture de chauffage*",
    img: "https://images.pexels.com/photos/20046693/pexels-photo-20046693.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Pompe à chaleur installée à l'extérieur d'une maison",
  },
  {
    n: "02",
    kicker: "La technologie",
    title: "La thermodynamique change tout",
    text: "Une pompe à chaleur air/eau ne brûle rien : elle capte les calories de l'air extérieur — même en hiver — pour chauffer votre maison et votre eau chaude. Pour 1 kWh d'électricité consommé, elle restitue jusqu'à 4 kWh de chaleur.",
    stat: "1 → 4",
    statLabel: "kWh restitué pour 1 kWh consommé (COP jusqu'à 4)",
    img: "/pac-garage.webp",
    alt: "Pompe à chaleur air/eau Alfea Excellia S Duo — unité extérieure et module intérieur dans un garage",
  },
  {
    n: "03",
    kicker: "Le financement",
    title: "Les aides rendent le saut accessible",
    text: "MaPrimeRénov', prime CEE, éco-PTZ, TVA à 5,5 % : cumulées, ces aides couvrent une grande partie du projet. Nous montons et déposons votre dossier avant même la signature du devis — vous n'avez rien à faire.",
    stat: "10 800 €",
    statLabel: "d'aides cumulées MaPrimeRénov' + CEE*",
    img: "/pac-maison.webp",
    alt: "Pompe à chaleur air/eau Alfea Excellia S Duo installée devant une maison",
  },
];

const Chapter = ({ c, index }) => (
  <FadeIn
    className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-16 ${
      index % 2 === 1 ? "" : ""
    }`}
  >
    <div className={index % 2 === 1 ? "lg:order-2" : ""}>
      <div className="flex items-center gap-4">
        <span className="text-stroke font-display text-6xl font-black leading-none sm:text-7xl">
          {c.n}
        </span>
        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-emerald-300 sm:text-xs">
          {c.kicker}
        </span>
      </div>
      <h3 className="mt-6 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
        {c.title}
      </h3>
      <p className="mt-5 max-w-xl text-base leading-relaxed text-white/65">
        {c.text}
      </p>
      <div className="mt-8 flex items-baseline gap-4 rounded-2xl border border-emerald-400/20 bg-emerald-400/5 px-6 py-5">
        <span className="font-display text-3xl font-extrabold text-emerald-300 sm:text-4xl">
          {c.stat}
        </span>
        <span className="text-xs leading-relaxed text-white/60 sm:text-sm">
          {c.statLabel}
        </span>
      </div>
    </div>

    <motion.div
      whileHover={{ scale: 1.015 }}
      transition={{ duration: 0.4 }}
      className={`relative ${index % 2 === 1 ? "lg:order-1" : ""}`}
    >
      <div className="absolute inset-0 translate-x-3 translate-y-3 rounded-2xl border border-emerald-400/30" />
      <div className="group relative overflow-hidden rounded-2xl">
        <img
          src={c.img}
          alt={c.alt}
          className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-deep/40 to-transparent opacity-60 transition-opacity duration-500 group-hover:opacity-30" />
      </div>
    </motion.div>
  </FadeIn>
);

export default function Manifesto() {
  return (
    <section data-testid="manifesto" className="relative bg-forest-deep py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <FadeIn>
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-emerald-300 sm:text-xs">
            Le manifeste
          </p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-5xl">
            Trois bonnes raisons de sauter le pas cette année.
          </h2>
        </FadeIn>
        <div className="mt-16 space-y-24 sm:mt-24 sm:space-y-32">
          {CHAPTERS.map((c, i) => (
            <Chapter key={c.n} c={c} index={i} />
          ))}
        </div>
        <p className="mt-16 text-[11px] text-white/35">
          *Selon vos revenus et votre projet — barèmes 2026, estimation
          indicative.
        </p>
      </div>
    </section>
  );
}
